<i18n>
{
  "fr": {
    "civility": "Civilité",
    "first-name": "Prénom",
    "last-name": "Nom",
    "nationality": "Nationalité",
    "birthday": "Date de naissance",
    "type": {
      "adt": "Adulte",
      "cnn": "Enfant",
      "inf": "Nourrison"
    },
    "traveller": "Voyageur",
    "additional-details": "Informations complémentaires",
    "errors": {
      "first-name": "Un prénom est requis",
      "last-name": "Un nom est requis",
      "nationality": "Une nationalité est requise",
      "nameLength": "Votre nom et prénom associés comportent plus de 46 caractères, ce qui ne vous permet pas de réserver en ligne sur notre site.<br>Nous vous remercions de bien vouloir faire parvenir votre demande par e-mail à <a href='mailto:airbus@amplitudes.com'>airbus@amplitudes.com</a> en précisant la raison de l'impossibilité de réserver en ligne. La subvention Online pourra ainsi bien être appliquée."
    }
  },
  "en": {
    "civility": "Civility",
    "first-name": "First name",
    "last-name": "Last name",
    "nationality": "Nationality",
    "birthday": "Birthday",
    "type": {
      "adt": "Adult",
      "cnn": "Children",
      "inf": "Infant"
    },
    "traveller": "Traveller",
    "additional-details": "Additional details",
    "errors": {
      "first-name": "A first name is required",
      "last-name": "A last name is required",
      "nationality": "A nationality is required",
      "nameLength": "Your surname and first name are longer than 46 characters, which means that you cannot book online on our website.<br>Please send your request by e-mail to <a href='mailto:airbus@amplitudes.com'>airbus@amplitudes.com</a> specifying the reason why you are unable to book online. The Online subsidy can then be applied."
    }
  }
}
</i18n>

<template>
  <v-card flat>
    <v-card-title>
      <h1 class="title">
        {{ title }}
      </h1>
    </v-card-title>
    <v-card-text>
      <v-row no-gutters>
        <v-col class="pr-2">
          <v-text-field
            v-model="t.civility"
            :label="$t('civility')"
            dense
            disabled
            height="42px"
            outlined
          />
        </v-col>
        <v-col class="px-2">
          <v-select
            v-model="t.first_name"
            :error-messages="firstNameErrors"
            :items="possibleFirstName"
            :label="$t('first-name')"
            :menu-props="{'offset-y': true, 'auto': true}"
            dense
            height="42px"
            outlined
            @blur="$v.t.first_name.$touch(); updateValidity()"
            @change="$v.t.first_name.$touch(); updateValidity()"
          >
            <template
              #label
            >
              {{ $t('first-name') }}
              <span
                v-if="!t.first_name"
                class="red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col class="px-2">
          <v-select
            v-model="t.last_name"
            :error-messages="lastNameErrors"
            :items="possibleLastName"
            :label="$t('last-name')"
            :menu-props="{'offset-y': true, 'auto': true}"
            dense
            height="42px"
            outlined
            @blur="$v.t.last_name.$touch(); updateValidity()"
            @change="$v.t.last_name.$touch(); updateValidity()"
          >
            <template
              #label
            >
              {{ $t('last-name') }}
              <span
                v-if="!t.last_name"
                class="red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col class="px-2">
          <v-select
            v-model="t.nationality"
            :error-messages="nationalityErrors"
            :items="countries"
            :label="$t('nationality')"
            :menu-props="{'offset-y': true, 'auto': true}"
            dense
            height="42px"
            item-text="name"
            item-value="alpha_2_code"
            outlined
            @blur="$v.t.nationality.$touch(); updateValidity()"
            @change="$v.t.nationality.$touch(); updateValidity()"
          >
            <template #item="{ item }">
              <div class="v-list-item__title">
                {{ item.alpha_2_code }} - {{ item.native_name }}
              </div>
            </template>
            <template
              #label
            >
              {{ $t('nationality') }}
              <span
                v-if="!t.nationality"
                class="red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col class="pl-2">
          <v-text-field
            v-model="t.birthday"
            :label="$t('birthday')"
            dense
            disabled
            height="42px"
            outlined
            type="date"
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="errorNameLength"
        dense
        border="left"
        type="warning"
        icon="mdi-alert-outline"
      >
        <span v-html="$t('errors.nameLength')" />
      </v-alert>
      <FlightPassportDetails
        v-if="needPassport"
        :traveller="traveller"
        @validity="passportValidity = $event"
      />
      <v-divider />
      <LuggageOptionsPerPassenger
        v-if="itinerary && !isLH"
        :age-at-departure="ageAtDeparture"
        :travel-type="travelType"
        :outward-included-luggage="itinerary.flight_outward.details[0].included_luggage"
        :return-included-luggage="itinerary.flight_return && itinerary.flight_return.details[0].included_luggage"
        :options-params="t.selected_luggage_option"
        @input="t.selected_luggage_option = $event"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters, mapState } from 'vuex'
import FlightPassportDetails from './FlightPassportDetails/FlightPassportDetails'
import moment from 'moment'
import LuggageOptionsPerPassenger
  from '@/components/Flight/FlightPassengersInformation/FlightTravellerDetails/LuggageOptionsPerPassenger/LuggageOptionsPerPassenger'

export default {
  name: 'FlightTravellerDetails',
  components: {
    LuggageOptionsPerPassenger,
    FlightPassportDetails
  },
  mixins: [validationMixin],
  props: {
    traveller: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    t: {},
    isLH: false,
    errorNameLength: false,
    passportValidity: false,
    possibleFirstName: [],
    possibleLastName: []
  }),
  computed: {
    ...mapState({
      outwardDate: state => state.fb.searchResults.outwardDate,
      travelType: state => state.fb.searchResults.travelType,
      itinerary: state => state.fb.searchResults.updatedSelectedItinerary
    }),
    ...mapGetters({
      countries: 'general/getCountries',
      needPassport: 'fb/searchResults/getPassportRequirements'
    }),
    /**
     * Return a title based on the traveller type (ADT | CNN | INF)
     * @return {string|undefined}
     */
    title () {
      let type
      if (this.traveller.traveller_type === 'ADT') {
        type = this.$i18n.t('type.adt')
      } else if (this.traveller.traveller_type === 'CNN') {
        type = this.$i18n.t('type.cnn')
      } else if (this.traveller.traveller_type === 'INF') {
        type = this.$i18n.t('type.inf')
      } else {
        return undefined
      }
      const traveller = this.$i18n.t('traveller')
      return `${traveller} ${this.index + 1} - ${type}`
    },
    /**
     * Calculate the age at departure of the traveller
     * @return {number}
     */
    ageAtDeparture () {
      const departureDate = moment(this.outwardDate)
      const birthday = moment(this.traveller.birthday)
      return departureDate.diff(birthday, 'years')
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    firstNameErrors () {
      const errors = []
      if (!this.$v.t.first_name.$dirty) return errors
      !this.$v.t.first_name.required && errors.push(this.$i18n.t('errors.first-name'))
      return errors
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    lastNameErrors () {
      const errors = []
      if (!this.$v.t.last_name.$dirty) return errors
      !this.$v.t.last_name.required && errors.push(this.$i18n.t('errors.last-name'))
      return errors
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    nationalityErrors () {
      const errors = []
      if (!this.$v.t.nationality.$dirty) return errors
      !this.$v.t.nationality.required && errors.push(this.$i18n.t('errors.nationality'))
      return errors
    },
    /**
     * Return the opposite of vuelidate invalid parameters (to improve readability of code)
     * Also verify the passport vuelidate value if a passport is needed
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    validity () {
      // console.log('SJISJI validity lenght : ', (this.t.last_name + ' ' + this.t.first_name).length)
      if (this.needPassport) {
        return !this.$v.$invalid && this.passportValidity && (this.t.last_name + ' ' + this.t.first_name).length <= 46
      } else {
        // console.log('SJISJI validity result : ', !this.$v.$invalid, !this.$v.$invalid && (this.t.last_name + ' ' + this.t.first_name).length <= 46)
        return !this.$v.$invalid && (this.t.last_name + ' ' + this.t.first_name).length <= 46
      }
    }
  },
  watch: {
    t: {
      /**
       * Must cloneDeep to avoid error with vueX (state modification outside store (JS Object reference))
       * @param newVal {Object}
       * @return {undefined}
       */
      handler: function (newVal) {
        this.updateTravellerDetails(_.cloneDeep(newVal))
      },
      deep: true
    },
    /**
     * Watch for value change of validity then commit the new value to the store
     * @param newVal {boolean}
     */
    validity (newVal) {
      this.updateTravellerDetailsValidity({
        registration_number: this.traveller.registration_number,
        validity: newVal
      })
    }
  },
  validations: {
    t: {
      first_name: { required },
      last_name: { required },
      nationality: { required }
    }
  },
  /**
   * Vue js lifecycle hook
   * Parse traveller properties and assign some to the component data
   */
  created () {
    this.t = {
      birthday: this.traveller.birthday,
      civility: this.traveller.civility,
      first_name: '',
      last_name: '',
      nationality: '',
      traveller_type: this.traveller.traveller_type,
      registration_number: this.traveller.registration_number,
      selected_luggage_option: _.cloneDeep(this.itinerary.luggage_options_params)
    }

    if (this.traveller.usage_first_name) {
      this.possibleFirstName = [this.traveller.first_name, this.traveller.usage_first_name]
    } else {
      this.t.first_name = this.traveller.first_name
      this.possibleFirstName = [this.traveller.first_name]
    }

    if (this.traveller.usage_last_name) {
      this.possibleLastName = [this.traveller.last_name, this.traveller.usage_last_name]
    } else {
      this.t.last_name = this.traveller.last_name
      this.possibleLastName = [this.traveller.last_name]
    }

    // si LH pas de bagages en option
    this.isLH = this.itinerary.flight_outward.segments[0].marketing_carrier === 'LH'

    // Have to update the validity of the traveller on created because validity watcher don't react (because it's already invalid)
    this.updateTravellerDetailsValidity({
      registration_number: this.traveller.registration_number,
      validity: false
    })
    this.updateValidity()
  },
  methods: {
    ...mapActions({
      updateTravellerDetails: 'fb/passengersInformation/changeSelectedTravellersDetails',
      updateTravellerDetailsValidity: 'fb/passengersInformation/changeSelectedTravellersDetailsValidity'
    }),
    updateValidity () {
      console.log('updateValidity')
      if ((this.t.last_name + ' ' + this.t.first_name).length <= 46) {
        this.errorNameLength = false
      } else {
        this.errorNameLength = true
      }
    }
  }
}
</script>

<style scoped>

</style>
